import ReactGA from "react-ga";
import ReactPixel from 'react-facebook-pixel';
import { GOOGLE_ANALYTICS_KEY, GTM_ID } from '../env';
import get from "lodash.get";
import { isArrayWithItems } from "./arrayHelper";
import {
  TrackerNames,
  ActionNames,
  TrackerKeys,
  GoogleTagManagerEventKeys,
  GoogleAnalyticsEvents,
  GoogleAnalyticsProperties,
} from "../constants/webTracking";
import { TagManager } from "../components/common/tagManager";
import { toSnakeCase } from "./toSnakeCase";

export class AnalyticsHelper {
  static initializeTrackers = (campaign: any) => {
    const webTrackingOptions = get(
      campaign,
      "template.value.webTrackingOptions"
    );
    const addGALinker = get(webTrackingOptions, "addGALinker");
    const gaLinkerDomain = get(webTrackingOptions, "gaLinkerDomain");

    ReactGA.initialize(AnalyticsHelper.getGATrackersList(campaign));
    ReactGA.plugin.require(`ecommerce`);
    // @ts-ignore

    if (addGALinker && isArrayWithItems(gaLinkerDomain)) {
      // @ts-ignore
      ReactGA.ga(`${TrackerNames.clientTrackerName}.require`, "linker");

      // @ts-ignore
      ReactGA.ga(`${TrackerNames.clientTrackerName}.linker:autoLink`, gaLinkerDomain);
    }

    // @ts-ignore
    ReactGA.ga(`${TrackerNames.clientTrackerName}.require`, "ecommerce");

    ReactGA.pageview(window.location.pathname, [
      TrackerNames.clientTrackerName,
    ]);

    // console.log("campaign", { campaign, webTracking: get(campaign, 'template.value.webTracking') })

    if (campaign && get(campaign, "template.value.webTracking")) {
      // Facebook
      const fpClientTracker = campaign.template.value.webTracking.find(
        (tracker: any) => {
          return tracker.key === TrackerKeys.Facebook;
        }
      );

      if (
        fpClientTracker &&
        fpClientTracker.value &&
        fpClientTracker.value.length > 0
      ) {
        ReactPixel.init(
          fpClientTracker.value,
          {} as any,
          {
            autoConfig: true, // set pixel's autoConfig
            debug: false, // enable logs
          }
        );
        ReactPixel.pageView();
      }

      // Google Tag Manager

      // Default tracker
      TagManager.initialize({
        gtmIds: [GTM_ID],
        dataLayer: { "gtm.whitelist ": ["google", "customPixels"] } as any,
      });

      // Client tracker
      const gtmClientTracker = campaign.template.value.webTracking.find(
        (tracker: any) => {
          return tracker.key === TrackerKeys.GoogleTagManager;
        }
      );

      if (
        gtmClientTracker &&
        gtmClientTracker.value &&
        gtmClientTracker.value.length > 0
      ) {
        TagManager.initialize({
          gtmIds: [gtmClientTracker.value],
          dataLayer: { "gtm.whitelist ": ["google", "customPixels"] } as any,
        });
      }
    }
  };

  static getGATrackersList = (model: any) => {
    const trackers = [
      {
        trackingId: GOOGLE_ANALYTICS_KEY || "",
        gaOptions: {
          allowLinker: false,
        },
      },
    ];

    if (model && get(model, "template.value.webTracking")) {
      const gaClientTracker = model.template.value.webTracking.find(
        (tracker: any) => {
          return tracker.key === TrackerKeys.Google;
        }
      );

      const webTrackingOptions = get(
        model,
        "template.value.webTrackingOptions"
      );
      const addGALinker = get(webTrackingOptions, "addGALinker");

      if (
        gaClientTracker &&
        gaClientTracker.value &&
        gaClientTracker.value.length > 0
      ) {
        trackers.push({
          trackingId: gaClientTracker.value,
          gaOptions: {
            name: TrackerNames.clientTrackerName,
            allowLinker: addGALinker ? addGALinker : false,
          },
        } as any);
      }
    }

    return trackers;
  };

  static createEvent = (category: any, action: any, label: any, value: any, currency: any) => {
    switch (action) {
      case ActionNames.join:
        ReactPixel.track("CompleteRegistration", {
          content_name: label,
          status: true,
          value,
          currency,
        });

        executeEvent(category, action, label);
        break;

      case ActionNames.sale:
        ReactPixel.track("Sale", {
          content_name: label,
          value,
          currency,
        });

        executeEvent(category, action, label);
        break;

      case ActionNames.donation:
        ReactPixel.track("Donate", {
          content_name: label,
          value,
          currency,
        });

        executeEvent(category, action, label);
        break;

      default: {
        console.log("Unknown event: ", action);
        break;
      }
    }
  };

  static executeEcommercePlugin = (
    id: any,
    campaignName: any,
    amount: any,
    currency: any,
    actionName: any,
    itemDetails: any
  ) => {
    /*
     * Google Tag Manager: Ecommerce Purchase Event
     * https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
     */
    TagManager.dataLayer({ dataLayer: { ecommerce: null } }); // clear ecommerce data before dataLayer merge
    if (!itemDetails) {
      TagManager.dataLayer({
        dataLayer: {
          event: GoogleTagManagerEventKeys.EcommerceEventTag,
          Event: GoogleTagManagerEventKeys.EcommerceEventTag,
          _event: GoogleTagManagerEventKeys.EcommerceEventTag,
          category: campaignName,
          action: GoogleAnalyticsEvents.Purchase,
          ecommerce: {
            transaction_id: `${actionName}_${id}`,
            value: amount,
            currency,
            items: [
              {
                item_name: actionName,
                item_category: campaignName,
                affiliation: GoogleAnalyticsProperties.Affiliation,
                price: amount,
                quantity: 1,
              },
            ],
          },
        },
      });
    } else {
      const items = itemDetails.map((item: any) => {
        const quantity = itemDetails.filter((i: any) => i.ticketId === item.ticketId)
          .length;
        return {
          item_id: item.ticketId,
          item_name: item.name,
          item_category: campaignName,
          affiliation: GoogleAnalyticsProperties.Affiliation,
          item_list_name: campaignName,
          price: item.price,
          quantity,
        };
      });
      TagManager.dataLayer({
        dataLayer: {
          event: GoogleTagManagerEventKeys.EcommerceEventTag,
          Event: GoogleTagManagerEventKeys.EcommerceEventTag,
          _event: GoogleTagManagerEventKeys.EcommerceEventTag,
          category: campaignName,
          action: GoogleAnalyticsEvents.Purchase,
          ecommerce: {
            transaction_id: `${actionName}_${id}`,
            value: amount,
            currency,
            items,
          },
        },
      });
    }

    // GA

    ReactGA.plugin.execute(`ecommerce`, "addTransaction", {
      id,
      revenue: amount,
      affiliation: campaignName,
      currency,
    });

    if (itemDetails == null) {
      ReactGA.plugin.execute(`ecommerce`, "addItem", {
        id,
        name: actionName,
        category: campaignName,
        price: amount,
        quantity: 1,
      });
    } else {
      itemDetails.forEach((item: any) => {
        let quantity = itemDetails.filter((i: any) => i.ticketId === item.ticketId)
          .length;
        ReactGA.plugin.execute(`ecommerce`, "addItem", {
          id,
          name: item.name,
          sku: item.ticketId,
          category: campaignName,
          price: item.price,
          quantity,
        });
      });
    }

    ReactGA.plugin.execute(`ecommerce`, "send", {});
    ReactGA.plugin.execute(`ecommerce`, "clear", {});

    // client tracker
    ReactGA.plugin.execute(
      `${TrackerNames.clientTrackerName}.ecommerce`,
      "addTransaction",
      {
        id,
        revenue: amount,
        affiliation: campaignName,
        currency,
      }
    );

    if (itemDetails == null) {
      ReactGA.plugin.execute(
        `${TrackerNames.clientTrackerName}.ecommerce`,
        "addItem",
        {
          id,
          name: actionName,
          category: campaignName,
          price: amount,
          quantity: 1,
        }
      );
    } else {
      itemDetails.forEach((item: any) => {
        let quantity = itemDetails.filter((i: any) => i.ticketId === item.ticketId)
          .length;
        ReactGA.plugin.execute(
          `${TrackerNames.clientTrackerName}.ecommerce`,
          "addItem",
          {
            id,
            name: item.name,
            sku: item.ticketId,
            category: campaignName,
            price: item.price,
            quantity,
          }
        );
      });
    }

    ReactGA.plugin.execute(
      `${TrackerNames.clientTrackerName}.ecommerce`,
      "send",
      {}
    );
    ReactGA.plugin.execute(
      `${TrackerNames.clientTrackerName}.ecommerce`,
      "clear",
      {}
    );
  };
}

export const executeEvent = (category: any, action: any, label: any) => {
  TagManager.dataLayer({
    dataLayer: {
      event: GoogleTagManagerEventKeys.CustomEventTag,
      Event: GoogleTagManagerEventKeys.CustomEventTag,
      _event: GoogleTagManagerEventKeys.CustomEventTag,
      action: toSnakeCase(action),
      category,
      label,
    },
  });

  ReactGA.event(
    {
      category,
      action,
      label,
    },
    [TrackerNames.clientTrackerName]
  );
};
